import { WebAuth } from 'auth0-js'
import getAuth0config, { redirectUri } from 'config'
import {
  GET_USER,
  ADD_USER,
  ACCEPT_GROUP_INVITATION,
  LOGIN_EVENT_USER,
} from 'graphql/queries/userEntities/userQueries'
import { useMutation, useQuery } from '@apollo/client'
import { client } from 'graphql/client'
import JWT from 'jwt-decode'
import localStorage from 'store'
import localforage from 'localforage'
import { useAuth0 } from 'config/react-auth0-spa'
import {
  ADD_PROJECT_MEMBERSHIP,
  JOIN_PROJECT_MEMBERSHIP,
} from 'graphql/queries/userEntities/projectMembershipQueries'
import { notification } from 'antd'
import { useHistory } from 'react-router-dom'
import { newUserInput } from 'graphql/util/newEntityFunctions'
import { useEffect, useCallback } from 'react'
import { GET_PROJECTS, GET_GROUP_PROJECTS } from 'graphql/queries/project/projectQueries'
import { IS_PRODUCTION, WORDPRESS_URL } from 'utils/constants'
import { handleMutationException } from 'utils/error'
import { firebaseAnalytics } from 'index'
import { dateOutput } from 'graphql/util/inputFunctions'
import { date } from 'utils/format'

import { useSetProject } from './projects'
import { DEFAULT_FETCH_POLICY } from './ApiContext'
import { registerEvent, loginEvent, useEvents } from './events'
import { useTranslate } from './utils'

export const useFirstLogin = () => {
  const [addUserAccount] = useMutation(ADD_USER, {
    update(cache, { data: { addUser } }) {
      cache.writeQuery({
        query: GET_USER,
        variables: { userId: addUser.userId },
        data: { user: addUser },
      })
    },
  })
  const acceptProjectInvite = useAcceptProjectInvitation()
  const acceptGroupInvite = useAcceptGroupInvitation()
  const history = useHistory()

  return (auth0User) => {
    // Context variables defined in rule script on auth0
    /* eslint-disable camelcase */
    const auth0_NS = 'https://ventury-analytics.de/'
    const firstname = auth0User[`${auth0_NS}firstName`]
    const lastname = auth0User[`${auth0_NS}lastName`]
    const newsSubscription = auth0User[`${auth0_NS}newsletter`] === 'true' // returned as string
    const userId = auth0User.sub
    /* eslint-enable camelcase */

    const newUserData = {
      userId,
      firstname,
      lastname,
      newsSubscription,
      email: auth0User.email,
    }

    registerEvent(newUserData) // Adding user GA-EVENT

    // 1.) Add new user
    addUserAccount({ variables: { userInput: newUserInput(newUserData) } }).then(() =>
      Promise.all([acceptProjectInvite(), acceptGroupInvite()]).then((values) => {
        // Only if no project/group invitation (null), redirect to guided tour
        if (values.every((n) => n === null)) {
          history.replace('/guide?showWelcome')
          notification.open({
            type: 'success',
            message: 'You have successfully logged in!',
            description: 'Welcome to Ventury Analytics!',
          })
        }
      }),
    )
  }
}

export const useAcceptGroupInvitation = () => {
  const [acceptGroupInvitation] = useMutation(ACCEPT_GROUP_INVITATION)
  const publishEvent = useEvents()
  const history = useHistory()
  const userId = getUserId()
  const translate = useTranslate()

  // Accept group invite & redirect
  return (groupId) => {
    const acceptGroupId = groupId || localStorage.get('accept_group_id')

    if (!acceptGroupId) return Promise.resolve(null)

    return acceptGroupInvitation({
      variables: { groupId: acceptGroupId },
      refetchQueries: [
        {
          query: GET_USER,
          variables: { userId },
          fetchPolicy: 'network-only',
        },
        {
          query: GET_PROJECTS,
          variables: { userId },
          fetchPolicy: 'network-only',
        },
      ],
      awaitRefetchQueries: true,
    })
      .then(() => {
        localStorage.remove('accept_group_id')
        history.replace('/portfolio')
        publishEvent('GROUP', 'ACCEPT_INVITATION')

        notification.open({
          type: 'success',
          message: translate('groupInvitationPage.accepted'),
          description: '',
        })
      })
      .catch((e) => {
        handleMutationException(e)
        history.replace('/portfolio')
      })
  }
}

export const useAcceptProjectInvitation = () => {
  const [addProjectMembership] = useMutation(ADD_PROJECT_MEMBERSHIP)
  const publishEvent = useEvents()
  const setProject = useSetProject()
  const history = useHistory()
  const userId = getUserId()

  // Accept project invite & redirect
  return (projectId) => {
    const acceptProjectId = projectId || localStorage.get('accept_project_id')

    if (!acceptProjectId) return Promise.resolve(null)

    return addProjectMembership({
      variables: { projectId: acceptProjectId, userId },
      refetchQueries: [
        {
          query: GET_PROJECTS,
          variables: { userId },
          fetchPolicy: 'network-only',
        },
      ],
      awaitRefetchQueries: true,
    })
      .then(() => {
        setProject(acceptProjectId)
        localStorage.remove('accept_project_id')
        history.replace('/overview')
        publishEvent('PROJECT', 'ACCEPT_INVITATION')

        notification.open({
          type: 'success',
          message: 'You have accepted the project invitation!',
          description: 'Explore your stakeholder position in the company.',
        })
      })
      .catch((e) => {
        handleMutationException(e)
        history.replace('/overview')
      })
  }
}

export const useJoinProject = () => {
  const [joinProject, { loading }] = useMutation(JOIN_PROJECT_MEMBERSHIP)
  const publishEvent = useEvents()
  const setProject = useSetProject()
  const history = useHistory()
  const { user, userId } = useCurrentAccount()
  const translate = useTranslate()

  // Accept project invite & redirect
  return [
    (projectId) => {
      if (!projectId) return Promise.resolve(null)

      return joinProject({
        variables: { projectId, userId },
        refetchQueries: [
          {
            query: GET_PROJECTS,
            variables: { userId },
            fetchPolicy: 'network-only',
          },
          {
            query: GET_GROUP_PROJECTS,
            variables: { groupId: user.group.id },
            fetchPolicy: 'network-only',
          },
        ],
      })
        .then(() => {
          setProject(projectId)
          history.replace('/overview')
          publishEvent('PROJECT', 'JOIN')

          notification.open({
            type: 'success',
            message: translate('project.joined.message'),
            description: translate('project.joined.description'),
          })
        })
        .catch((e) => {
          handleMutationException(e)
        })
    },
    { loading },
  ]
}

export const hasValidToken = () => {
  const token = getDecodedToken()

  // 1) no token
  if (!token) return false
  const expiresAt = token.exp
  const isExpired = Date.now() >= expiresAt * 1000 // By definition: https://stackoverflow.com/questions/51292406/jwt-check-if-token-expired

  // 2) expired token
  return !isExpired
}

export const getUserId = () => {
  const token = getDecodedToken()
  return token ? token.sub : null
}

export const getDecodedToken = () => {
  const token = localStorage.get('access_token')
  return token ? JWT(token) : null
}

export const useCurrentAccount = () => {
  const userId = getUserId()
  // 1) Needs to fetch data (await token if not in local storage)
  const { loading, data, error } = useQuery(GET_USER, {
    fetchPolicy: DEFAULT_FETCH_POLICY,
    variables: { userId },
    skip: !userId,
  }) // load user from server
  const { user } = useAuth0()
  const firstLogin = useFirstLogin()
  const [loginEventUser] = useMutation(LOGIN_EVENT_USER)

  useEffect(() => {
    // 2) First login and user needs to be created
    if (user && data && data.user === null) firstLogin(user)
    else if (data && data.user) {
      firebaseAnalytics.setUserId(userId)
      loginEvent(data.user)

      // synchronize latest login field with auth0
      if (user && date(user.updated_at).diff(date(data.user.lastLogin), 'hours') > 0) {
        loginEventUser({
          variables: {
            userId: data.user.userId,
            loginTime: dateOutput(user.updated_at),
          },
        })
      }
    }
  }, [user, data])

  // 3) Return user data (or loading state)
  return { user: data ? data.user : null, userId, loading, error }
}

// TODO: Simple old version, does new version always work properly? Why Client not initialized
// export const useLogout = () => {
//   const { logout } = useAuth0()

//   const doLogout = () => {
//     // Reset store to remove sensitive project data from machine
//     client.cache.reset()
//     localStorage.clearAll()
//     localforage.clear()

//     if (logout) {
//       logout({ returnTo: IS_PRODUCTION ? WORDPRESS_URL : redirectUri() })
//     }
//   }

//   return doLogout
// }

export const useLogout = () => {
  const { loading, logout } = useAuth0()

  const clear = () => {
    // Reset store to remove sensitive project data from machine
    client.cache.reset()
    localStorage.clearAll()
    localforage.clear()
  }

  const doLogout = (depth = 0, resolve, reject) => {
    // Prevents crash if client is not initialized
    if (!loading) {
      clear()
      logout({ returnTo: IS_PRODUCTION ? WORDPRESS_URL : redirectUri() })
      return resolve ? resolve() : Promise.resolve()
    }

    // Try repeatedly if client is initialized
    if (depth < 5) {
      return new Promise((resolve, reject) => {
        setTimeout(() => doLogout(depth + 1, resolve, reject), 100)
      })
    }

    // Logout failed
    clear()
    const err = new Error('Auth0-Client not successfully initialized.')
    return reject ? reject(err) : Promise.reject(err)
  }

  return doLogout
}

export const useResetPassword = () => {
  const history = useHistory()
  const resetPassword = useCallback((email) => {
    // NOTE: Not supported by SPA-auth0, thus extra client needed - only initiate if a request is made (seldom)
    const auth0config = getAuth0config(history)
    const auth0 = new WebAuth({
      domain: auth0config.domain,
      clientID: auth0config.client_id,
      redirectUri: auth0config.redirect_uri,
      audience: auth0config.audience,
      onRedirectCallback: auth0config.onRedirectCallback,
    })
    return new Promise((resolve, reject) => {
      auth0.changePassword(
        {
          connection: 'Username-Password-Authentication',
          email,
        },
        (err, res) => {
          console.log('RESPONSE', err, res)
          if (err) {
            reject(err)
          }
          resolve(res)
        },
      )
    })
  }, [])

  return resetPassword
}
